import * as _ from 'lodash';
import moment from 'moment/moment';
import React from 'react';
import { Panel } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SmoothScrollbar from 'smooth-scrollbar';
import i18n from '../../../config/i18n';
import { fire } from '../../../event-bus';
import CreateShipmentButtonSeparately from '../../../extension/take-shipment/view/component/order/order-detail/CreateShipmentButtonSeparately';
import CoreComponent from '../../../framework/component/CoreComponent';
import CoreContainer from '../../../framework/container/CoreContainer';
import ComponentFactory from '../../../framework/factory/ComponentFactory';
import ContainerFactory from '../../../framework/factory/ContainerFactory';
import layout from '../../../framework/Layout';
import DateTimeHelper from '../../../helper/DateTimeHelper';
import NumberHelper from '../../../helper/NumberHelper';
import OrderHelper from '../../../helper/OrderHelper';
import Permission from '../../../helper/Permission';
import TaxHelper from '../../../helper/TaxHelper';
import CustomerService from '../../../service/customer/CustomerService';
import LocationService from '../../../service/LocationService';
import OrderGiftCardPopupService from '../../../service/sales/order/order-gift-card/OrderGiftCardPopupService';
import OrderService from '../../../service/sales/OrderService';
import OrderWeeeDataService from '../../../service/weee/OrderWeeeDataService';
import CreditmemoAction from '../../action/order/CreditmemoAction';
import OrderAction from '../../action/OrderAction';
import StatusConstant from '../../constant/order/StatusConstant';
import PaymentConstant from '../../constant/PaymentConstant';
import PermissionConstant from '../../constant/PermissionConstant';
import BillingAddress from './order-detail/detail-content/BillingAddress';
import CommentItem from './order-detail/detail-content/CommentItem';
import ContentPaymentItem from './order-detail/detail-content/ContentPaymentItem';
import ShippingAddress from './order-detail/detail-content/ShippingAddress';
import ShippingMethod from './order-detail/detail-content/ShippingMethod';

export class OrderDetail extends CoreComponent {
  static className = 'OrderDetail';

  setBlockContentElement = (element) => {
    this.block_content = element;
    if (!this.scrollbarOrderDetail) {
      this.scrollbarOrderDetail = SmoothScrollbar.init(this.block_content);
    }
  };

  /**
   *
   * @param props
   */
  constructor(props) {
    super(props);
    this.state = {
      canCancel: false,
      canTakePayment: false,
      canRefund: false,
      printBtnClassName: 'btn btn-default',
      isRefunding: false,
      canShip: true, // Estado inicial do botão
      isPartialShipment: false, // Novo estado para "Baixa Iniciada"
    };
  }

  /**
   * Component will receive props
   * @param nextProps
   */
  componentWillReceiveProps(nextProps) {
    if (this.props.order !== nextProps.order) {
      if (nextProps.order && !nextProps.order.items) {
        return;
      }
      if (this.scrollbarOrderDetail) {
        this.scrollbarOrderDetail.scrollTo(0, 0);
      }

      let paymentStatus = OrderService.getPaymentStatus(nextProps.order);
      let abilities = {
        canCancel: OrderService.canCancel(nextProps.order),
        canTakePayment:
          paymentStatus.realValue ===
            StatusConstant.PAYMENT_STATUS_PARTIAL_PAID ||
          paymentStatus.realValue === StatusConstant.PAYMENT_STATUS_UNPAID,
        canRefund:
          OrderService.canCreditmemo(nextProps.order) &&
          this.isAllowed(PermissionConstant.PERMISSION_REFUND),
      };

      fire(
        'component_order_detail_component_will_receive_props_calculate_order_abilities_after',
        { abilities, nextProps },
      );
      this.setState(abilities);

      if (this.state.isRefunding) {
        this.setState({ isRefunding: false });
        this.props.openCreditmemoOrder();
      }
    }
  }

  /**
   * component will unmount
   */
  componentWillUnmount() {
    if (this.scrollbarOrderDetail) {
      SmoothScrollbar.destroy(this.scrollbarOrderDetail);
    }
  }

  /**
   * refund
   */
  async refund() {
    if (!this.state.canRefund) {
      return;
    }

    let orderHasGiftCard = OrderHelper.hasGiftCardItems(this.props.order);

    if (!orderHasGiftCard) {
      return this.props.openCreditmemoOrder();
    }

    if (!window.navigator.onLine) {
      return toast.error(
        i18n.translator.translate(
          'Connection failed. ' +
            'You must connect to a Wi-Fi ' +
            'or cellular data network to refund order contains Gift card product',
        ),
        {
          className: 'wrapper-messages messages-warning',
          autoClose: 3000,
        },
      );
    }

    let { order } = this.props;

    this.setState({ isRefunding: true });

    OrderGiftCardPopupService.check(order, {}, (orderData) => {
      this.props.actions.updateOrder(orderData);
    });
  }

  /**
   * handle click take payment
   */
  handleClickTakePayment() {
    if (this.state.canTakePayment) {
      this.props.openTakePayment();
    }
  }

  /**
   * handle click cancel order
   */
  handleClickCancelOrder() {
    let deepCanCancel = OrderService.deepCanCancel(this.props.order);
    if (deepCanCancel.canCancel) {
      return this.props.openCancelOrder();
    }

    if (!deepCanCancel.message) {
      return;
    }

    return this.props.openOrderAlertPopup(deepCanCancel);
  }

  /**
   * handle click add note
   */
  handleClickAddComment() {
    this.props.openAddComment();
  }

  /**
   * handle click send email
   */
  handleClickSendEmail() {
    this.props.openSendEmail();
  }

  /**
   * handle click reorder
   */
  async handleClickReorder() {
    this.props.startReorder();
  }

  /**
   * Print order by order data
   * @param order
   * @returns {Promise.<void>}
   */
  async handleClickPrint(order) {
    const needLoadCustomer = !order.customer_is_guest;
    if (!needLoadCustomer) {
      this.props.actions.reprintOrder(order, null);
    } else {
      try {
        this.setState({
          printBtnClassName: 'btn btn-default loader',
        });
        let customer = await CustomerService.getById(order.customer_id);
        this.setState({
          printBtnClassName: 'btn btn-default',
        });
        this.props.actions.reprintOrder(order, customer);
      } catch (e) {
        this.props.actions.reprintOrder(order, null);
      }
    }
  }

  /**
   * get display location
   * @param order
   * @return {*}
   */
  getDisplayLocation(order) {
    if (
      Permission.isAllowed(
        PermissionConstant.PERMISSION_MANAGE_ORDER_CREATED_AT_ALL_LOCATION,
      ) ||
      Permission.isAllowed(
        PermissionConstant.PERMISSION_MANAGE_ALL_ORDERS_IN_SYSTEM,
      )
    ) {
      let locationId = order.pos_location_id
        ? order.pos_location_id
        : order.warehouse_id;
      if (locationId) {
        let location = LocationService.getLocationById(locationId);
        if (location) {
          return this.props.t('Location: {{name}}', { name: location.name });
        }
      }
    }
    return '';
  }

  /**
   * get discount amount
   * @param order
   * @returns {number}
   */
  getDiscountAmount(order) {
    return order && order.discount_amount ? Math.abs(order.discount_amount) : 0;
  }

  /**
   * Return loading flag value
   *
   * @return boolean
   */
  isLoading() {
    return this.props.isDetailLoading;
  }

  handleBaixa = (customer) => {
    const btnTakeShipment = document.querySelector('.btn-order-take-shipment');
    if (btnTakeShipment) {
      btnTakeShipment.click();
    } else {
      console.warn('Botão .btn-order-take-shipment não encontrado!');
    }
  };
  calculateIsPartialShipment(order) {
    if (!order || !order.items) return false;

    // Verifica se há algum item com qty_left > 0
    const hasItemsLeft = order.items.some((item) => item.qty_left > 0);
    //console.log(hasItemsLeft, 'HASITEMLEFTDETAILS');

    return hasItemsLeft;
  }
  // checkIfAllItemsShipped = (order) => {
  //   console.log("childrenItems recebidos:", order.items);

  //   const allShipped = order.items.every(child => {
  //       console.log(`Item ${child.sku} - qty_left:`, order.items.qty_left);
  //       return order.items.qty_left === 0;
  //   });

  //   console.log("Todos os itens foram enviados?", allShipped);
  //   return allShipped;
  // };

  updateShipmentStatus() {
    const { order } = this.props;
    console.log(order.item, 'itemorderdetail');
    if (!order || !order.items) return;

    // Verifica se todos os itens foram retirados
    const allItemsShipped = order.items.every((item) => item.qty === 0);

    // Verifica se pelo menos um item foi retirado, mas nem todos
    const isPartialShipment =
      order.items.some((item) => item.qty > 0) && !allItemsShipped;

    // Atualiza o estado
    this.setState({
      canShip: !allItemsShipped, // canShip será false apenas quando todos os itens forem retirados
      isPartialShipment, // isPartialShipment será true se pelo menos um item foi retirado, mas nem todos
    });
  }

  /**
   * template
   * @returns {*}
   */
  template() {
    let { order, isLoading, t } = this.props;
    if (order && !order.items) {
      order = null;
    }
    let billingAddress = OrderService.getBillingAddress(order);
    let created_at = order
      ? moment(
          DateTimeHelper.convertDatabaseDateTimeToLocalDate(order.created_at),
        ).format('L LT')
      : '';

    // let due =
    // order &&
    // order.total_due > 0 &&
    // order.state !== StatusConstant.STATE_CANCELED &&
    // order.state !== StatusConstant.STATE_CLOSED
    //   ? this.props.t("Due: {{total_due}}", {
    //       total_due: OrderHelper.formatPrice(order.total_due, order),
    //     })
    //   : "";

    let giftcardDiscount =
      order && order.gift_voucher_gift_codes ? order.gift_voucher_discount : 0;
    let discount = this.getDiscountAmount(order) - giftcardDiscount;

    let weeeTotal =
      order && order.items
        ? OrderWeeeDataService.getTotalAmounts(order.items, order)
        : '';

    let paymentStatus = OrderService.getPaymentStatus(order);

    let statusHistory =
      order && order.status_histories ? order.status_histories : [];
    statusHistory = _.orderBy(statusHistory, 'created_at', 'desc');

    let customer_name =
      order &&
      order.customer_firstname &&
      order.customer_lastname &&
      !order.customer_is_guest
        ? order.customer_firstname + ' ' + order.customer_lastname
        : this.props.t('Guest');
    let telephone =
      order && !order.customer_is_guest && billingAddress
        ? billingAddress.telephone || t('N/A')
        : null;
    let other_payment;
    if (order && order.payments) {
      let actutal_total_paid = 0;
      order.payments.forEach((payment) => {
        if (payment.type !== PaymentConstant.TYPE_REFUND) {
          actutal_total_paid = NumberHelper.addNumber(
            payment.amount_paid,
            actutal_total_paid,
          );
        }
      });
      if (actutal_total_paid < order.total_paid) {
        let amount = order.total_paid - actutal_total_paid;
        other_payment = {
          amount_paid: amount,
          base_amount_paid: amount,
          card_type: null,
          is_paid: 1,
          method: 'otherpayment',
          payment_date: null,
          receipt: null,
          reference_number: null,
          title: 'Other',
          type: 0,
        };
      }
    }

    return (
      <div className="wrapper-order-right">
        <div className="block-title title-s">
          {order ? order.increment_id : ''}
          {!order ? (
            isLoading || this.isLoading() ? (
              <div />
            ) : (
              <div className="page-notfound">
                <div className="icon"></div>
                {this.props.showNoInternet
                  ? this.props.t('Please connect to Internet to find orders')
                  : this.props.t('No order is found.')}
              </div>
            )
          ) : (
            <span className="order-price-t">
              {OrderHelper.formatPrice(order.grand_total, order)}
            </span>
          )}
        </div>

        <div
          className="loader-product"
          style={{
            display: this.isLoading() ? 'block' : 'none',
            marginTop: '300px',
            marginBottom: '500px',
          }}
        ></div>

        <div
          data-scrollbar
          ref={this.setBlockContentElement}
          className="block-content"
        >
          {!order ? (
            isLoading || this.isLoading() ? (
              <div />
            ) : (
              <div className="page-notfound">
                <div className="icon"></div>
                {this.props.showNoInternet
                  ? this.props.t('Please connect to Internet to find orders')
                  : this.props.t('No order is found.')}
              </div>
            )
          ) : (
            <div>
              <div className="order-info">
                {/* <div className="order-price">
                  <div className="price">
                    {OrderHelper.formatPrice(order.grand_total, order)}
                  </div>
                  <div>
                    <span className="price-label due">{due}</span>
                  </div>
                </div> */}
                <div className="row">
                  <div className="col-sm-6">
                    <div className="order-detail">
                      <div>
                        <span className="order-detail-label">
                          {this.props.t('Order Date:')}
                        </span>
                        <span className="order-detail-value">{created_at}</span>
                      </div>

                      {/* <div>
                        <span className="order-detail-label">
                          {this.props.t("Location:")}
                        </span>
                        <span className="order-detail-value">
                          {this.getDisplayLocation(order)}
                        </span>
                      </div> */}

                      <div>
                        <span className="order-detail-label">
                          {this.props.t('Customer Name:')}
                        </span>
                        <span className="order-detail-value">
                          {customer_name}
                        </span>
                      </div>

                      {telephone ? (
                        <div>
                          <span className="order-detail-label">
                            {this.props.t('Customer Phone:')}
                          </span>
                          <span className="order-detail-value">
                            {telephone}
                          </span>
                        </div>
                      ) : (
                        telephone
                      )}

                      {order.pos_staff_name && (
                        <div className="old">
                          <span className="order-detail-label">
                            {this.props.t('Staff:')}
                          </span>
                          <span className="order-detail-value">
                            {order.pos_staff_name}
                          </span>
                        </div>
                      )}

                      {layout('order')('orderHistory')('staff_name_layout')(
                        'order_detail_staff_name_after',
                      )()(this)}

                      <div className="order-status">
                        <span
                          className={
                            'status ' +
                            order.status.toLowerCase().replace(' ', '-')
                          }
                        >
                          {this.props.t(
                            OrderService.getDisplayStatus(
                              order.state,
                              order.status,
                            ),
                          )}
                        </span>
                        <span className={'status ' + paymentStatus.className}>
                          {this.props.t(paymentStatus.value)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="order-total">
                      <ul>
                        <li>
                          <span className="title">
                            {this.props.t('Subtotal')}
                          </span>
                          <span className="value">
                            {OrderService.getDisplaySubtotal(order)}
                          </span>
                        </li>
                        <li>
                          <span className="title">
                            {OrderHelper.getDiscountDisplay(order)}
                          </span>
                          <span className="value">
                            -{OrderHelper.formatPrice(discount, order)}
                          </span>
                        </li>

                        {layout('order')('order_detail_layout')(
                          'total_discount_after',
                        )()(this)}

                        {!order.is_virtual ? (
                          <li>
                            <span className="title">
                              {this.props.t('Shipping')}
                            </span>
                            <span className="value">
                              {OrderService.getDisplayShippingAmount(order)}
                            </span>
                          </li>
                        ) : null}
                        {giftcardDiscount ? (
                          <li>
                            <span className="title">{t('Gift Card')}</span>
                            <span className="value">
                              {OrderHelper.formatPrice(
                                -giftcardDiscount,
                                order,
                              )}
                            </span>
                          </li>
                        ) : (
                          ''
                        )}
                        <li>
                          <span className="title">{this.props.t('FPT')}</span>
                          <span className="value">
                            {OrderHelper.formatPrice(weeeTotal, order)}
                          </span>
                        </li>
                        {!TaxHelper.orderDisplayZeroTaxSubTotal() &&
                        order.tax_amount === 0 ? null : (
                          <li>
                            <span className="title">{this.props.t('Tax')}</span>
                            <span className="value">
                              {OrderHelper.formatPrice(order.tax_amount, order)}
                            </span>
                          </li>
                        )}

                        {layout('order')('order_detail_layout')(
                          'total_tax_after',
                        )()(this)}

                        <li>
                          <span className="title">
                            {this.props.t('Grand Total')}
                          </span>
                          <span className="value">
                            {OrderHelper.formatPrice(order.grand_total, order)}
                          </span>
                        </li>

                        {layout('order')('order_detail_layout')(
                          'grand_total_after',
                        )()(this)}

                        <li>
                          <span className="title">
                            {this.props.t('Total Paid')}
                          </span>
                          <span className="value">
                            {OrderHelper.formatPrice(order.total_paid, order)}
                          </span>
                        </li>
                        {order.pos_change ? (
                          <li>
                            <span className="title">
                              {this.props.t('Total Change')}
                            </span>
                            <span className="value">
                              {OrderHelper.formatPrice(order.pos_change, order)}
                            </span>
                          </li>
                        ) : null}
                        {order.total_refunded ? (
                          <li>
                            <span className="title">
                              {this.props.t('Total Refunded')}
                            </span>
                            <span className="value">
                              {OrderHelper.formatPrice(
                                order.total_refunded,
                                order,
                              )}
                            </span>
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="accordion"
                className="panel-group"
                role="tablist"
                aria-multiselectable="true"
              >
                <Panel>
                  <Panel.Heading>
                    <Panel.Title toggle>
                      {this.props.t('Items Ordered')}
                    </Panel.Title>
                  </Panel.Heading>

                  <Panel.Body>
                    <div
                      className="all-items-container"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                      }}
                    >
                      {(() => {
                        const groupedItems = [];
                        let currentGroup = null;

                        order.items
                          .filter(
                            (item) =>
                              item.product_id !== 18594 &&
                              item.product_id !== 22229,
                          )
                          .forEach((item) => {
                            if (
                              item.associated_customers &&
                              item.associated_customers.length > 0
                            ) {
                              currentGroup = {
                                customer: item.associated_customers[0],
                                items: [item],
                                squadColor: item.ext_order_item_id,
                                numberChest: item.additional_data,
                                t_shirt_name: item.t_shirt_name,
                              };

                              groupedItems.push(currentGroup);
                            } else if (currentGroup) {
                              currentGroup.items.push(item);
                            }
                          });

                        const formatDate = (dob) => {
                          if (!dob) return 'N/A';
                          const [year, month, day] = dob.split('-');
                          return `${day}/${month}/${year}`;
                        };

                        const formatCPF = (cpf) => {
                          if (!cpf || cpf.length !== 11) return cpf || 'N/A';
                          return cpf.replace(
                            /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
                            '$1.$2.$3-$4',
                          );
                        };

                        const getsquadColorStyle = (squadColor) => {
                          switch (squadColor) {
                            case 'Verde':
                              return { backgroundColor: '#03BC01' };
                            case 'Branco':
                              return {
                                backgroundColor: 'white',
                                color: '#000000',
                                border: '1px solid #000000',
                              };
                            case 'Azul':
                              return {
                                backgroundColor: '#01ABF7',
                                color: 'white',
                                padding: '5px',
                                borderRadius: '3px',
                              };
                            case 'Quênia':
                              return {
                                backgroundColor: '#000000',
                                color: 'white',
                              };
                            default:
                              return {
                                backgroundColor: 'gray',
                                color: 'white',
                                padding: '5px',
                                borderRadius: '3px',
                              };
                          }
                        };

                        return groupedItems.map((group, index) => {
                          // Encontra o bundle (item principal) no grupo
                          const bundleItem = group.items.find(
                            (item) => !item.parent_item_id,
                          );
                          const parentId = bundleItem
                            ? bundleItem.item_id
                            : null; // Captura o item_id do bundle (que será o parent_id dos filhos)

                          return (
                            <div
                              key={index}
                              className={`customer-group ${
                                group.items.some(
                                  (item) =>
                                    item.associated_customers &&
                                    item.associated_customers.length > 0 &&
                                    item.associated_customers[0].group_id === 4,
                                )
                                  ? 'prime-item-background'
                                  : ''
                              }`}
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '20px',
                              }}
                            >
                              <div
                                className="customer-info"
                                style={{ flex: 1 }}
                              >
                                {group.customer && (
                                  <div className="associated-customers">
                                    <span className="attribute-label">
                                      {this.props.t('Participant')}:
                                    </span>
                                    <span className="attribute-value">
                                      {group.customer.firstname}{' '}
                                      {group.customer.lastname}
                                    </span>
                                    <div className="additional-info">
                                      <span className="attribute-label">
                                        {this.props.t('Data de Nascimento:')}
                                      </span>
                                      <span className="attribute-value">
                                        {formatDate(group.customer.dob)}
                                      </span>
                                    </div>
                                    <div className="additional-info">
                                      <span className="attribute-label">
                                        {this.props.t('Documento:')}
                                      </span>
                                      <span className="attribute-value">
                                        {formatCPF(group.customer.taxvat)}
                                      </span>
                                    </div>
                                    <div
                                      className="squadColor"
                                      style={getsquadColorStyle(
                                        group.squadColor,
                                      )}
                                    >
                                      <span
                                        className="attribute-label"
                                        style={{
                                          color:
                                            group.squadColor === 'Branco'
                                              ? '#000000'
                                              : 'inherit',
                                        }}
                                      >
                                        {this.props.t('Pelotão: ')}
                                      </span>
                                      <span
                                        className="attribute-value"
                                        style={{
                                          color: !group.squadColor
                                            ? '#fff'
                                            : 'inherit',
                                        }}
                                      >
                                        {group.squadColor
                                          ? group.squadColor
                                          : 'Atribuir'}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div
                                className="items-container"
                                style={{
                                  flex: 2,
                                  display: 'flex',
                                  gap: '10px',
                                }}
                              >
                                {(() => {
                                  const groupA = group.items.filter((item) =>
                                    ['24', '23', '28', '31'].includes(
                                      item.attribute_set_id,
                                    ),
                                  );
                                  const groupB = group.items.filter((item) =>
                                    [
                                      '44',
                                      '9',
                                      '30',
                                      '33',
                                      '24',
                                      '23',
                                    ].includes(item.attribute_set_id),
                                  );
                                  const groupC = group.items.filter(
                                    (item) => item.attribute_set_id === '27',
                                  );

                                  return (
                                    <React.Fragment>
                                      {/* 🔹 Grupo A */}
                                      <div
                                        className="itemOrder"
                                        style={{ flex: 1 }}
                                      >
                                        {groupA.length > 0 ? (
                                          groupA.map((item, idx) => (
                                            <div key={idx} className="group-a">
                                              <div className="item-name">
                                                {/* Exibir "KIT:" para o item com attribute_set_id 24 */}
                                                {item.attribute_set_id ===
                                                  '24' && (
                                                  <span className="item-label">
                                                    KIT:{' '}
                                                  </span>
                                                )}
                                                {/* Exibir o nome do KIT (attribute_set_id 24) */}
                                                {item.attribute_set_id ===
                                                  '24' && (
                                                  <span className="item-name">
                                                    {item.name}
                                                  </span>
                                                )}

                                                {/* Exibir "Bateria:" para o item com attribute_set_id 31 */}
                                                {item.attribute_set_id ===
                                                  '31' && (
                                                  <span className="item-label">
                                                    Bateria:{' '}
                                                  </span>
                                                )}
                                                {/* Exibir o valor da Bateria (attribute_set_id 31) */}
                                                {item.attribute_set_id ===
                                                  '31' &&
                                                  item.name && (
                                                    <span className="attribute-value">
                                                      {item.name}
                                                    </span>
                                                  )}

                                                {/* Exibir a distância para o item com attribute_set_id 28 */}
                                                {item.attribute_set_id ===
                                                  '28' &&
                                                  item.distance && (
                                                    <div className="item-details">
                                                      <span className="item-label">
                                                        {this.props.t(
                                                          'Distância',
                                                        )}
                                                        :
                                                      </span>
                                                      <span className="distance-value">
                                                        {item.distance}
                                                      </span>
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="empty-group">
                                            <p style={{ visibility: 'hidden' }}>
                                              -
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      {/* 🔹 Grupo B */}
                                      <div
                                        className="itemOrder"
                                        style={{ flex: 1 }}
                                      >
                                        {/* Renderizar Personalização apenas uma vez */}
                                        {group.t_shirt_name && (
                                          <div className="group-b personalization">
                                            <div className="item-name">
                                              <span className="item-label">
                                                Personalização:{' '}
                                              </span>

                                              <span className="attribute-value">
                                                {group.t_shirt_name}
                                              </span>
                                            </div>
                                          </div>
                                        )}

                                        {groupB.length > 0 ? (
                                          groupB.map((item, idx) => (
                                            <div key={idx} className="group-b">
                                              <div className="item-name">
                                                {/* Exibir "Jaqueta" para o item com attribute_set_id 44 */}
                                                {item.attribute_set_id ===
                                                  '44' && (
                                                  <React.Fragment>
                                                    <span className="item-label">
                                                      Jaqueta:{' '}
                                                    </span>
                                                    {item.size && (
                                                      <span className="attribute-value">
                                                        {item.size}
                                                      </span>
                                                    )}
                                                  </React.Fragment>
                                                )}

                                                {/* Exibir "Boné" para o item com attribute_set_id 33 */}
                                                {item.attribute_set_id ===
                                                  '33' && (
                                                  <React.Fragment>
                                                    <span className="item-label">
                                                      Boné:{' '}
                                                    </span>
                                                    {item.name && (
                                                      <span className="attribute-value">
                                                        {item.name}
                                                      </span>
                                                    )}
                                                  </React.Fragment>
                                                )}
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="empty-group">
                                            <p style={{ visibility: 'hidden' }}>
                                              -
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      {/* 🔹 Grupo C - Camisetas (27) */}
                                      <div
                                        className="itemOrder"
                                        style={{ flex: 1 }}
                                      >
                                        {/* Renderiza o numberChest fora do loop do groupC */}
                                        {group.numberChest && (
                                          <div className="number-chest">
                                            <span className="attribute-label">
                                              Nº de Peito{' '}
                                            </span>
                                            <span className="attribute-value">
                                              {group.numberChest}
                                            </span>
                                          </div>
                                        )}

                                        {groupC.length > 0 ? (
                                          groupC.map((item, idx) => (
                                            <div
                                              key={idx}
                                              className="group-camiseta"
                                            >
                                              <div className="item-label">
                                                <span className="attribute-label">
                                                  Camiseta{' '}
                                                </span>
                                                <span className="attribute-value">
                                                  {item.size || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="empty-group">
                                            <p style={{ visibility: 'hidden' }}>
                                              -
                                            </p>
                                          </div>
                                        )}

                                        {/* 🔹 Botão aparece sempre */}
                                        <div className="action-buttons-container">
                                          <CreateShipmentButtonSeparately
                                            key={order.entity_id}
                                            order={order}
                                            associatedCustomer={parentId} // Passa o parent_id
                                            canShip={this.state.canShip}
                                            isPartialShipment={this.calculateIsPartialShipment(
                                              order,
                                            )}
                                            setIsCreatingShipment={
                                              this.props.setIsCreatingShipment
                                            }
                                            afterLoadProductQtys={() => {
                                              if (this.state.canShip) {
                                                this.props.setIsCreatingShipment(
                                                  true,
                                                );
                                              }
                                              this.setState({
                                                isModalOpen: true,
                                                selectedOrder: order,
                                                associatedCustomer: parentId,
                                              });
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </React.Fragment>
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </Panel.Body>
                </Panel>
                <div className="panel-flex">
                  <Panel eventKey="2" key={Math.random()}>
                    <Panel.Heading>
                      <Panel.Title toggle>
                        {this.props.t('Payment Method')}
                      </Panel.Title>
                    </Panel.Heading>
                    <Panel.Body collapsible>
                      <div className="text-right">
                        <span className={'status ' + paymentStatus.className}>
                          {paymentStatus.value}
                        </span>
                      </div>
                      <ul className="payment-method">
                        {order.payments
                          ? order.payments.map((payment, index) =>
                              payment.type !== PaymentConstant.TYPE_REFUND ? (
                                <ContentPaymentItem
                                  key={index}
                                  payment={payment}
                                  order={order}
                                />
                              ) : null,
                            )
                          : null}
                        {other_payment ? (
                          <ContentPaymentItem
                            payment={other_payment}
                            order={order}
                          />
                        ) : null}
                      </ul>
                    </Panel.Body>
                  </Panel>
                  {!order.is_virtual ? (
                    <Panel eventKey="3" key={Math.random()}>
                      <Panel.Heading>
                        <Panel.Title toggle>
                          {this.props.t('Shipping Method')}
                        </Panel.Title>
                      </Panel.Heading>
                      <Panel.Body collapsible>
                        <ShippingMethod order={order} />
                      </Panel.Body>
                    </Panel>
                  ) : null}
                  {!order.is_virtual ? (
                    <Panel eventKey="4" key={Math.random()}>
                      <Panel.Heading>
                        <Panel.Title toggle>
                          {this.props.t('Shipping Address')}
                        </Panel.Title>
                      </Panel.Heading>
                      <Panel.Body collapsible>
                        <ShippingAddress order={order} />
                      </Panel.Body>
                    </Panel>
                  ) : null}
                  <Panel eventKey="5" key={Math.random()}>
                    <Panel.Heading>
                      <Panel.Title toggle>
                        {this.props.t('Billing Address')}
                      </Panel.Title>
                    </Panel.Heading>
                    <Panel.Body collapsible>
                      <BillingAddress order={order} />
                    </Panel.Body>
                  </Panel>
                </div>
                {statusHistory && statusHistory.length ? (
                  <Panel eventKey="6" key={Math.random()}>
                    <Panel.Heading>
                      <Panel.Title toggle>
                        {this.props.t('Comment History')}
                      </Panel.Title>
                    </Panel.Heading>
                    <Panel.Body collapsible>
                      <ul className="comment-history">
                        {statusHistory.map((comment, index) => (
                          <CommentItem
                            key={comment.entity_id + '_' + index}
                            comment={comment}
                          />
                        ))}
                      </ul>
                    </Panel.Body>
                  </Panel>
                ) : null}
              </div>
            </div>
          )}
        </div>
        {order ? (
          <div className="block-actions">
            <ul
              className={
                'actions' +
                (!this.isAllowed(PermissionConstant.PERMISSION_REFUND)
                  ? ' hidden-refund'
                  : '')
              }
            >
              {layout('order')('order_detail_layout')('actions')(
                'take_payment_before',
              )()(this)}
              <li>
                <button
                  className={
                    'btn btn-default ' +
                    (!this.state.canTakePayment ? 'disabled' : '')
                  }
                  onClick={() => this.handleClickTakePayment()}
                >
                  {this.props.t('Take Payment')}
                </button>
              </li>
              {layout('order')('order_detail_layout')('actions')(
                'refund_before',
              )()(this)}
              <li
                className={
                  !this.isAllowed(PermissionConstant.PERMISSION_REFUND)
                    ? 'hidden'
                    : ''
                }
              >
                <button
                  className={
                    'btn btn-default ' +
                    (!this.state.canRefund ? 'disabled' : '')
                  }
                  onClick={() => this.refund()}
                >
                  {this.props.t('Refund')}
                </button>
              </li>
              {layout('order')('order_detail_layout')('actions')(
                'cancel_before',
              )()(this)}
              <li>
                <button
                  className={
                    'btn btn-default ' +
                    (!this.state.canCancel ? 'disabled' : '')
                  }
                  onClick={() => this.handleClickCancelOrder()}
                >
                  {this.props.t('Cancel')}
                </button>
              </li>
              {layout('order')('order_detail_layout')('actions')(
                'print_before',
              )()(this)}
              <li>
                <button
                  className={this.state.printBtnClassName}
                  onClick={() => this.handleClickPrint(order)}
                >
                  {this.props.t('Print')}
                </button>
              </li>
              {layout('order')('order_detail_layout')('actions')(
                'email_before',
              )()(this)}
              <li>
                <button
                  className="btn btn-default"
                  onClick={() => this.handleClickSendEmail()}
                >
                  {this.props.t('Email')}
                </button>
              </li>
              {layout('order')('order_detail_layout')('actions')(
                'add_note_before',
              )()(this)}
              <li>
                <button
                  className="btn btn-default"
                  onClick={() => this.handleClickAddComment()}
                >
                  {this.props.t('Add Note')}
                </button>
              </li>
              {layout('order')('order_detail_layout')('actions')(
                'reorder_before',
              )()(this)}
              <li>
                <button
                  className="btn btn-default"
                  onClick={() => this.handleClickReorder()}
                >
                  {this.props.t('Reorder')}
                </button>
              </li>
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
}

class OrderDetailContainer extends CoreContainer {
  static className = 'OrderDetailContainer';

  static mapDispatch(dispatch) {
    return {
      actions: {
        reprintOrder: (order, customer) =>
          dispatch(OrderAction.reprintOrder(order, customer)),
        createCreditmemo: (creditmemo) =>
          dispatch(CreditmemoAction.createCreditmemo(creditmemo)),
        updateOrder: (orderData) =>
          dispatch(OrderAction.syncActionUpdateDataFinish([orderData])),
      },
    };
  }
}

export default ContainerFactory.get(OrderDetailContainer).withRouter(
  ComponentFactory.get(OrderDetail),
);



// WEBPACK FOOTER //
// ./src/view/component/order/OrderDetail.js